import { useMemo, type ReactNode } from 'react';
import type { MultiCellSelection, MultiSelectCellRef } from '../../availability';
import type { UnifiedSelectCellResolution } from '../../availability/lib/unified-select-types';
import { buildQueueJoinSection } from './build-queue-join-section';
import { resolveSelectQueueContext } from './resolve-select-queue-context';
import type {
  QueueJoinClientOption,
  QueueJoinOfferOption,
  QueueJoinPanelLabels,
} from './queue-join-panel';
import { useCellQueueJoin } from './use-cell-queue-join';

export interface UseSelectQueueJoinOptions {
  selection: MultiCellSelection | null;
  resolveCell: (ref: MultiSelectCellRef) => UnifiedSelectCellResolution | null;
  testId: string;
  clientOptions: QueueJoinClientOption[];
  offerOptions?: QueueJoinOfferOption[];
  labels: QueueJoinPanelLabels;
}

export interface UseSelectQueueJoinResult {
  /** Passed straight to `UnifiedSelectPopover`'s `queueJoinSection` prop. */
  queueJoinSection: ReactNode;
  /** Passed straight to `UnifiedSelectPopover`'s `queueJoinLoading` prop. */
  queueJoinLoading: boolean;
}

/** Wires the SELECT popover's own queue-join slot (CU-86cbejhae) — a `useCellQueueJoin`
 * context separate from the INSPECT popover's, driven by the single own-held selected cell. */
export function useSelectQueueJoin({
  selection,
  resolveCell,
  testId,
  clientOptions,
  offerOptions,
  labels,
}: UseSelectQueueJoinOptions): UseSelectQueueJoinResult {
  const context = useMemo(
    () => resolveSelectQueueContext(selection, resolveCell),
    [selection, resolveCell],
  );
  const queue = useCellQueueJoin(context);
  const queueJoinSection = buildQueueJoinSection(context.enabled, testId, {
    activeQueueState: queue.activeQueueState,
    currentUserId: queue.currentUserId,
    queueListId: context.mediaUnitId,
    clientOptions,
    offerOptions,
    labels,
    onJoin: queue.handleJoin,
    onQueueAction: queue.handleQueueAction,
    isJoinPending: queue.isJoinPending,
  });
  return { queueJoinSection, queueJoinLoading: queue.isQueueLoading };
}
